import React from 'react'
import { desktopAssets } from 'src/containers/PreCache'
import styled from 'styled-components'
import WinnerCard from './WinnerCard'
import StartLuckyDraw from './Start'
import { fetchLuckyDrawWinners, LuckyDrawWinner } from 'src/apis/luckyDrawApi'
import Confetti from 'react-confetti-boom'

const LuckyDrawPage: React.FC = () => {
  const [startDraw, setStartDraw] = React.useState(false)
  const [winners, setWinners] = React.useState<LuckyDrawWinner[]>([])
  const [loading, setLoading] = React.useState(false)
  const [startAnimation, setStartAnimation] = React.useState(false)
  const [revealedWinners, setRevealedWinners] = React.useState<Set<number>>(new Set())
  const [showFinaleConfetti, setShowFinaleConfetti] = React.useState(false)

  const handleWinnerRevealed = (winnerId: number) => {
    setRevealedWinners((prev) => {
      const newSet = new Set(prev)
      newSet.add(winnerId)

      // Check if all winners have been revealed
      if (newSet.size === winners.length && winners.length > 0) {
        // Trigger finale confetti after a short delay
        setTimeout(() => {
          setShowFinaleConfetti(true)
        }, 1000)
      }

      return newSet
    })
  }

  const handleStartDraw = async () => {
    setStartDraw(true)
    setLoading(true)

    try {
      const response = await fetchLuckyDrawWinners()
      if (response.success) {
        setWinners(response.winners_data)
        // Start animation immediately after data is loaded
        setTimeout(() => {
          setStartAnimation(true)
        }, 200) // Very short delay just to ensure DOM is updated
      }
    } catch (error) {
      console.error('Failed to fetch lucky draw winners:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Wrapper>
      <div className="header">
        <h1>Lucky Draw</h1>
      </div>
      <Container id="container">
        <div className="spacer"></div>
        {!startDraw ? (
          <StartLuckyDraw onStart={handleStartDraw} />
        ) : (
          <Content id="content">
            {loading ? (
              <div style={{ color: 'white', textAlign: 'center', gridColumn: '1 / -1' }}>Loading winners...</div>
            ) : (
              winners.map((winner, index) => (
                <WinnerCard
                  key={winner.id}
                  winner={winner.winner}
                  dummyUsers={winner.dummyUsers.map((user) => user.agentCode)}
                  startAnimation={startAnimation}
                  delay={index * 300} // 300ms delay between each card
                  onWinnerRevealed={() => handleWinnerRevealed(winner.id)}
                />
              ))
            )}
          </Content>
        )}
      </Container>
      {/* Finale confetti when all winners are revealed */}
      {showFinaleConfetti && (
        <Confetti
          mode="fall"
          particleCount={300}
          shapeSize={15}
          colors={['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b', '#eb4d4b', '#6c5ce7', '#a55eea', '#26de81', '#fd79a8']}
        />
      )}
    </Wrapper>
  )
}

export default LuckyDrawPage

const Wrapper = styled.div`
  background-image: url(${desktopAssets.luckyDrawBg});
  background-size: 100% 100%;
  background-position: top;
  width: 100%;
  min-height: 100dvh;

  .header {
    display: flex;
    width: 100%;
    height: 159px;
    padding: 13px 21px;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
    flex-shrink: 0;
    border: 2px solid var(--Game_Stroke, #70ecf4);
    background: linear-gradient(299deg, #0f407b -12.8%, #70ecf4 106.16%);
    box-shadow: 0 -9px 43.5px -4px rgba(112, 236, 244, 0.91), 0 4px 4px 0 rgba(0, 0, 0, 0.25);

    h1 {
      text-align: center;
      color: #fff;
      text-align: center;
      font-size: 4rem;
      font-style: normal;
      font-weight: 900;
      line-height: normal;
      text-transform: uppercase;
      text-align: center;
    }
  }
`

const Container = styled.div`
  width: 100%;
  padding-inline: 16px;

  .spacer {
    height: 98px;
  }
`

const Content = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  justify-content: center;
  column-gap: 39px;
  row-gap: 61px;
  padding: 20px;
`
